import { useEffect, useState } from 'react'
import { Alert, Button, Col, Collapse, Container, FloatingLabel, Form, Row } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import NoReports from '../components/NoReports'
import UserSelector from '../components/UserSelector'
import Activity from '../components/Activity'
import { useGetReportsQuery } from '../redux/api/brainApi'
import {
  useGetCategoriesQuery,
  useGetCharacterQuery,
  useGetDocRecommendationsQuery,
  useGetInsightsQuery,
  useGetProfileDataQuery,
} from '../redux/api/eegReportApi'
import { useGetSpecialitiesQuery } from '../redux/api/specialityApi'
import Assessment from './components/Assessment'
import BrainDominance from './components/BrainDominance'
import CareerInsights from './components/CareerInsights'
import CognitiveDescription from './components/CognitiveDescription'
import CognitiveProfile from './components/CognitiveProfile'
import EmotionalMentalAnalysis from './components/EmotionalMentalAnalysis'
import FrontPage from './components/FrontPage'
import LearningStyle from './components/LearningStyle'
import './components/report.css'
import { compareAsc, format, parse } from 'date-fns'
import useGetReports from './useGetReports'
import clientConfig from '../ClientConfig'
import Recommendations from './components/Recommendations'
import DoctorRecommendations from './components/DoctorRecommendations'
import AddRecommendationModal from './components/AddRecommendationModal'

const PersonalityInsights = () => {
  let initialValues = {
    category: 'all',
    speciality: 'all',
    gender: 'all',
    date: 'all',
    minAge: 2,
    maxAge: 99,
  }
  const [filters, setFilters] = useState(initialValues)
  const [show, setShow] = useState(false)
  const [DOA, setDOA] = useState('NA')
  const [modalVisible, setModalVisible] = useState(false)

  const { user } = useSelector((state) => state.auth)
  const { selectedUser } = useSelector((state) => state.user)

  const { reports, loading: isReports } = useGetReports({ id: selectedUser?.id })

  // Define skip conditions for dependent queries
  const shouldSkip = !reports || isReports || reports.length === 0

  const { data: categories, isLoading: isCategories } = useGetCategoriesQuery({}, { skip: shouldSkip })

  const { data: specialities, isLoading: isSpecialities } = useGetSpecialitiesQuery({}, { skip: shouldSkip })

  const { data, isLoading, isError, error } = useGetProfileDataQuery(
    {
      isGroup: localStorage.getItem('isGroup') || false,
      userId: selectedUser?.id,
      ...filters,
    },
    { skip: shouldSkip }
  )

  const {
    data: character,
    isLoading: isCharacter,
    isError: isCharacterError,
    error: characterError,
  } = useGetCharacterQuery({ userId: selectedUser?.id }, { skip: shouldSkip })

  const {
    data: insights,
    isLoading: isInsights,
    isError: isInsightsError,
    error: insightsError,
  } = useGetInsightsQuery({ userId: selectedUser?.id }, { skip: shouldSkip })

  const {
    data: recommendations = [],
    isError: isRecommendationError,
    error: recommendationError,
  } = useGetDocRecommendationsQuery({ userId: selectedUser?.id }, { skip: !selectedUser })

  useEffect(() => {
    if (!clientConfig?.[user.name]) {
      return
    }
    const content = clientConfig[user.name]?.footer
    document.documentElement.style.setProperty('--powered-by-content', `"${content}"`)
  }, [user.name])

  useEffect(() => {
    if (reports?.length) {
      const parsedDates = reports.map((item) => parse(item.date, 'yyyy.MM.dd HH.mm', new Date()))

      const latestDate = parsedDates.reduce((a, b) => (compareAsc(a, b) > 0 ? a : b))

      const formattedDate = format(latestDate, 'dd-MM-yyyy')

      setDOA(formattedDate)
    }
  }, [reports])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFilters((old) => ({ ...old, [name]: value }))
  }

  const reset = () => setFilters(initialValues)

  const handleModalOpen = () => setModalVisible(true)
  const handleModalClose = () => setModalVisible(false)

  return (
    <>
      <div className='d-print-none'>
        <div className='dashboard-heading'>
          <div className='content-container'>
            <h2>Report</h2>
            <p>Here’s a quick overview for your reports</p>
          </div>
          <Activity />
        </div>
        <p className='m-0'>Showing results for:</p>
        <UserSelector />
      </div>

      {isReports ? (
        <p>Loading...</p>
      ) : !reports || reports.length === 0 ? (
        <div className='d-flex justify-content-center'>
          <NoReports />
        </div>
      ) : (
        <Container fluid='lg'>
          {/* Filters */}
          <div className='d-print-none bg-white d-flex flex-column' style={{ position: 'sticky', top: '-24px', zIndex: 1 }}>
            <div className='d-flex justify-content-between'>
              <button className='fs-5 px-4 py-2 border-0 common-btn' onClick={handleModalOpen}>
                Doctor Recommendation
              </button>
              <div>
                <button className='rounded-pill px-4 py-2 border-0 text-white me-2' style={{ background: 'orange' }} onClick={() => window.print()}>
                  Download
                </button>
                <button
                  onClick={() => setShow((p) => !p)}
                  className='rounded-pill px-4 py-2 border-0'
                  style={{
                    background: 'var(--green-highlight-color)',
                    color: 'var(--green-color)',
                  }}
                >
                  Filter
                </button>
              </div>
            </div>
            <Collapse in={show}>
              <div>
                <Row className='p-4'>
                  <Col md={6} lg={4} xl={3}>
                    <FloatingLabel controlId='floatingSelect' label='Category'>
                      <Form.Select required value={filters.category} onChange={handleChange} name='category'>
                        <option value='all'>Any</option>
                        {isCategories
                          ? 'Loading...'
                          : Object.values(categories)?.map((c, i) => {
                              return (
                                <option key={i} value={c}>
                                  {c}
                                </option>
                              )
                            })}
                      </Form.Select>
                    </FloatingLabel>
                  </Col>
                  <Col md={6} lg={4} xl={3}>
                    <FloatingLabel controlId='floatingSelect' label='Speciality'>
                      <Form.Select required value={filters.speciality} onChange={handleChange} name='speciality'>
                        <option value='all'>Any</option>
                        {isSpecialities
                          ? 'Loading...'
                          : specialities?.map((s, i) => {
                              return (
                                <option key={i} value={s}>
                                  {s}
                                </option>
                              )
                            })}
                      </Form.Select>
                    </FloatingLabel>
                  </Col>
                  <Col md={6} lg={4} xl={3}>
                    <FloatingLabel controlId='Date' label='Date'>
                      <Form.Select required value={filters.date} onChange={handleChange} name='date'>
                        <option value='all'>Any</option>
                        <option value='this_week'>This Week</option>
                        <option value='this_month'>This Month</option>
                        <option value='this_year'>This Year</option>
                      </Form.Select>
                    </FloatingLabel>
                  </Col>
                  <Col md={6} lg={4} xl={3}>
                    <FloatingLabel controlId='floatingSelect' label='Gender'>
                      <Form.Select required value={filters.gender} onChange={handleChange} name='gender'>
                        <option value='all'>Any</option>
                        <option value='male'>Male</option>
                        <option value='female'>Female</option>
                        <option value='prefer_not_to_say'>Prefer Not To Say</option>
                      </Form.Select>
                    </FloatingLabel>
                  </Col>
                  <Col md={6} lg={4} xl={3}>
                    <FloatingLabel label='Min Age'>
                      <Form.Control type='number' min={2} max={99} onChange={handleChange} name='minAge' value={filters.minAge} />
                    </FloatingLabel>
                  </Col>
                  <Col md={6} lg={4} xl={3}>
                    <FloatingLabel label='Max Age'>
                      <Form.Control type='number' min={2} max={99} onChange={handleChange} name='maxAge' value={filters.maxAge} />
                    </FloatingLabel>
                  </Col>
                  <Col md={6} lg={4} xl={3}>
                    <Button className='common-btn w-100 py-3' onClick={reset}>
                      Reset
                    </Button>
                  </Col>
                </Row>
              </div>
            </Collapse>
          </div>
          {isLoading || isCharacter || isInsights ? (
            'Loading...'
          ) : isError || isCharacterError || isInsightsError ? (
            <Alert variant='danger'>{error?.data?.error || characterError?.data?.error || insightsError?.data?.error} </Alert>
          ) : !data ? (
            'No data'
          ) : (
            <div className='pdf-page-container'>
              <div className='content-container'>
                <FrontPage selectedUser={selectedUser} DOA={DOA} />
                <Assessment charts={data.charts} character={character} selectedUser={selectedUser} />
                <CognitiveDescription />
                <BrainDominance data={insights.all_text} selectedUser={selectedUser} />
                {user.headsetName !== 'SenzeBand2' && <CognitiveProfile {...data} selectedUser={selectedUser} />}
                <LearningStyle data={data.charts.Learning_Style} selectedUser={selectedUser} />
                {user.headsetName !== 'SenzeBand2' && (
                  <>
                    <EmotionalMentalAnalysis charts={data.charts} />
                    <CareerInsights data={insights.top3} />
                  </>
                )}
                <Recommendations profileData={data} />
                {recommendations?.length > 0 && <DoctorRecommendations data={recommendations} />}
              </div>
            </div>
          )}
          <AddRecommendationModal show={modalVisible} handleClose={handleModalClose} />
        </Container>
      )}
    </>
  )
}

export default PersonalityInsights
